import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleLogin, googleLogout, useGoogleLogin } from '@react-oauth/google';
import { config } from '../../config/appConfig';

const Topbar = ({ notifications = [], openModal }) => {
  const location = useLocation();
  const { isAuthenticated, user, logout, login, isLoading: isAuthLoading } = useAuth();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false); // <-- Add state for dropdown
  const navigate = useNavigate();
  let leaveTimeout; // To add a small delay before closing dropdown

  const modules = [
      { name: 'RCON Hacking', path: '/rcon-hacking', icon: 'fa-terminal', color: 'text-accent-pink' },
      { name: 'Data Extraction', path: '/server-data-extraction', icon: 'fa-database', color: 'text-accent-blue' },
      { name: 'DDoS Testing', path: '/ddos', icon: 'fa-network-wired', color: 'text-accent-red' },
      { name: 'OP Bot', path: '/bots?type=op', icon: 'fa-user-shield', color: 'text-accent-purple' },
      { name: 'Crash Bot', path: '/bots?type=crash', icon: 'fa-bolt', color: 'text-accent-orange' },
      { name: 'Account Bot', path: '/bots?type=account', icon: 'fa-key', color: 'text-accent-yellow' },
      { name: 'Bots', path: '/bots', icon: 'fa-user-shield', color: 'text-accent-purple' },
      { name: 'Account Checker', path: '/account-checker', icon: 'fa-user-check', color: 'text-accent-green' },
      { name: 'Servers Database', path: '/servers', icon: 'fa-server', color: 'text-accent-blue' },
      { name: 'Secret Method', path: '/secret', icon: 'fa-mask', color: 'text-accent-pink' },
      { name: 'My Account', path: '/account', icon: 'fa-user-circle', color: 'text-accent-purple' }
  ];

  const handleGoogleCredentialResponse = useCallback(async (credentialResponse) => {
      const idToken = credentialResponse.credential;
      if (!idToken) { console.error('Google Login did not return a credential.'); return; }
      console.log("ID Token to send:", idToken.substring(0, 20) + "...");
      try {
          const backendUrl = config.api.auth.googleVerify;
          const response = await axios.post(backendUrl, { token: idToken });
          if (response.data && response.data.jwtToken) { login(response.data.jwtToken); }
          else { console.error('Backend did not return a valid JWT.'); }
      } catch (error) {
          if (error.response) {
              console.error('Error sending Google token - Status:', error.response.status, 'Data:', error.response.data);
          } else if (error.request) { console.error('Error sending Google token - No response:', error.request); }
          else { console.error('Error sending Google token - Request setup:', error.message); }
      }
  }, [login]);

  const handleLogout = () => { googleLogout(); logout(); };
  const handleGoogleLoginError = (error) => { console.error('Google Login Failed:', error); };

  const getPageName = () => {
      const path = location.pathname;
      const currentModule = modules.find(m => m.path === path);
      if (currentModule) return currentModule.name;
      if (path === '/') return 'Home';
      return 'Dashboard';
  };

  useEffect(() => {
      if (searchQuery.trim() === '') { setSearchResults([]); setShowSearchResults(false); return; }
      const filteredResults = modules.filter(module => module.name.toLowerCase().includes(searchQuery.toLowerCase()));
      setSearchResults(filteredResults); setShowSearchResults(true);
  }, [searchQuery]);

  useEffect(() => {
      const handleClickOutside = (e) => {
          if (!e.target.closest('.search-container') && !e.target.closest('.search-results-dropdown')) { setShowSearchResults(false); }
          if (!e.target.closest('.notification-button') && !e.target.closest('.notification-dropdown')) { setShowNotifications(false); }
          if (!e.target.closest('.account-dropdown-container')) { setIsAccountDropdownOpen(false); } // Close account dropdown
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleModuleSelect = (path) => { navigate(path); setSearchQuery(''); setShowSearchResults(false); };
  const unreadCount = notifications.filter(notification => !notification.read).length;

  // Dropdown Mouse Handlers
  const handleMouseEnterAccount = () => {
      clearTimeout(leaveTimeout);
      setIsAccountDropdownOpen(true);
  };
  const handleMouseLeaveAccount = () => {
      leaveTimeout = setTimeout(() => {
          setIsAccountDropdownOpen(false);
      }, 150); // Small delay to allow moving to dropdown
  };


  return (
      <header className="h-20 px-8 flex items-center justify-between border-b border-dark-400 sticky top-0 z-30 bg-dark-800 backdrop-blur-sm bg-opacity-80">
          {/* Page Title */}
          <div>
              <h1 className="text-xl font-medium bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
                  {getPageName()}
              </h1>
          </div>

          {/* Search and Actions */}
          <div className="flex items-center gap-4">
              {/* Search */}
              <div className={`relative transition-all duration-300 search-container ${isSearchFocused || showSearchResults ? 'w-72' : 'w-64'}`}>
                  <input
                      type="text" placeholder="Search modules..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className={`bg-dark-600 rounded-full py-2 px-4 pl-10 text-sm focus:outline-none transition-all duration-200 w-full ${isSearchFocused ? 'ring-2 ring-accent-purple/50 border-accent-purple border' : 'border border-dark-400 hover:border-dark-300'}`}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setTimeout(() => { if (!showSearchResults) setIsSearchFocused(false) }, 150)}
                  />
                  <i className={`fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${isSearchFocused ? 'text-accent-purple' : 'text-gray-400'}`}></i>
                  {/* Search Results Dropdown */}
                  <AnimatePresence>
                      {showSearchResults && searchResults.length > 0 && (
                          <motion.div
                              initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                              className="absolute top-full left-0 right-0 mt-2 bg-dark-600 rounded-xl shadow-xl border border-dark-400 overflow-hidden z-50 search-results-dropdown">
                              {searchResults.map((module, index) => (
                                  <div key={index} onClick={() => handleModuleSelect(module.path)} className="px-4 py-2 hover:bg-dark-500 cursor-pointer text-sm flex items-center">
                                      <i className={`fas ${module.icon} ${module.color} w-4 mr-2`}></i> {module.name}
                                  </div>
                              ))}
                          </motion.div>
                      )}
                      {showSearchResults && searchResults.length === 0 && searchQuery.trim() !== '' && (
                          <motion.div
                              initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                              className="absolute top-full left-0 right-0 mt-2 bg-dark-600 rounded-xl shadow-xl border border-dark-400 overflow-hidden z-50 search-results-dropdown">
                              <div className="px-4 py-2 text-sm text-gray-400">No results found.</div>
                          </motion.div>
                      )}
                  </AnimatePresence>
              </div>

              {/* Notifications */}
              <div className="relative notification-button">
                  <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      className="relative p-2 rounded-full hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-800 focus:ring-accent-purple"
                      aria-label="Notifications"
                  >
                      <i className="fas fa-bell text-gray-400 hover:text-white"></i>
                      {unreadCount > 0 && (<div className="absolute top-1 right-1 w-2 h-2 bg-accent-red rounded-full animate-ping"></div>)}
                      {unreadCount > 0 && (<div className="absolute top-1 right-1 w-2 h-2 bg-accent-red rounded-full"></div>)}
                  </button>
                  <AnimatePresence>
                      {showNotifications && (
                          <motion.div
                              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}
                              className="absolute top-full right-0 mt-2 w-80 bg-dark-600 rounded-xl shadow-xl border border-dark-400 z-50 overflow-hidden notification-dropdown">
                              <div className="p-3 border-b border-dark-400"><h4 className="text-sm font-medium">Notifications</h4></div>
                              <div className="max-h-80 overflow-y-auto custom-scrollbar">
                                  {notifications.length === 0 ? (<p className="text-center text-gray-400 text-sm py-4">No new notifications</p>) : (
                                      notifications.map(notification => (
                                          <div key={notification.id} className={`p-3 flex items-start border-b border-dark-500 hover:bg-dark-500 transition-colors ${!notification.read ? 'bg-dark-500/50' : ''}`}>
                                              <div className={`w-8 h-8 rounded-full bg-${notification.color}/20 text-${notification.color} flex items-center justify-center mr-3 flex-shrink-0 mt-1`}><i className={`fas ${notification.icon}`}></i></div>
                                              <div>
                                                  <p className="text-sm font-medium">{notification.title}</p>
                                                  <p className="text-xs text-gray-400">{notification.message}</p>
                                                  <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                                              </div>
                                          </div>
                                      ))
                                  )}
                              </div>
                              <div className="p-2 text-center border-t border-dark-400"><button className="text-xs text-accent-blue hover:underline">View All</button></div>
                          </motion.div>
                      )}
                  </AnimatePresence>
              </div>

              {/* --- Authentication Section --- */}
              {isAuthLoading ? (
                  <div className="w-10 h-10 flex items-center justify-center"><i className="fas fa-spinner fa-spin text-gray-400"></i></div>
              ) : isAuthenticated && user ? (
                  // --- Authenticated State (with hover fix) ---
                  <div
                      className="relative account-dropdown-container" // Add container class
                      onMouseEnter={handleMouseEnterAccount}
                      onMouseLeave={handleMouseLeaveAccount}
                  >
                      <button className="flex items-center space-x-2 cursor-pointer p-1 rounded-full hover:bg-dark-600">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-accent-purple to-accent-blue flex items-center justify-center text-sm font-bold">
                              {user.email ? user.email.substring(0, 1).toUpperCase() : '?'}
                          </div>
                          <span className="text-sm font-medium hidden md:inline">{user.email}</span>
                          <i className={`fas fa-chevron-down text-xs text-gray-400 hidden md:inline transition-transform ${isAccountDropdownOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      {/* Dropdown */}
                      <AnimatePresence>
                          {isAccountDropdownOpen && (
                              <motion.div
                                  initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.15 }}
                                  className="absolute top-full right-0 mt-2 w-48 bg-dark-600 rounded-xl shadow-xl border border-dark-400 z-50">
                                  <button onClick={() => { navigate('/account'); setIsAccountDropdownOpen(false); }} className="block w-full text-left px-4 py-2 text-sm hover:bg-dark-500 transition-colors rounded-t-xl">
                                      <i className="fas fa-user-cog mr-2"></i>My Account
                                  </button>
                                  <button onClick={() => { handleLogout(); setIsAccountDropdownOpen(false); }} className="block w-full text-left px-4 py-2 text-sm hover:bg-dark-500 text-accent-red transition-colors rounded-b-xl">
                                      <i className="fas fa-sign-out-alt mr-2"></i>Logout
                                  </button>
                              </motion.div>
                          )}
                      </AnimatePresence>
                  </div>
              ) : (
                  // --- Unauthenticated State ---
                  <GoogleLogin onSuccess={handleGoogleCredentialResponse} onError={handleGoogleLoginError} useOneTap />
              )}
          </div>
      </header>
  );
};

export default Topbar;